import { executeSoon } from '@streamr/tv-utils';
import { hexToBinary } from '@streamr/utils';
import { EventEmitter } from 'eventemitter3';
import { Env } from '../../Env';
import { StreamrTvBroadcaster } from '../../engine/StreamrTvBroadcaster';
import { assert } from '../../utils/assert';
import { BroadcastStateService } from '../broadcaststate/BroadcastStateService';
import { MediaService } from '../media/MediaService';
import { PluginApiService } from '../pluginapi/PluginApiService';
import { ViewerCountService } from '../viewercount/ViewerCountService';
import { BroadcastServiceOutput } from './BroadcastServiceOutput';
export class BroadcastService extends EventEmitter {
    static instance;
    static getInstance() {
        if (!BroadcastService.instance) {
            BroadcastService.instance = new BroadcastService();
        }
        return BroadcastService.instance;
    }
    static getNotificationInterface() {
        return BroadcastService.getInstance();
    }
    static getEmitterInterface() {
        return BroadcastService.getInstance();
    }
    static getOutput() {
        return BroadcastServiceOutput.getReactiveOutput();
    }
    // ------------- Singleton implementation end
    mediaService;
    streamrTvBroadcaster;
    broadcastStarted = false;
    broadcastActivated = false;
    currentStreamId;
    // Output state to be used by the React components
    output;
    canvas;
    constructor() {
        super();
        this.output = new BroadcastServiceOutput();
        this.mediaService = MediaService.getInstance();
        this.mediaService.on('mediaStreamChanged', this.onMediaStreamChanged);
        PluginApiService.getInstance().on('videoFrameProcessed', this.onVideoFrameProcessed);
        const activeBroadcast = BroadcastStateService.getInstance().getActiveBroadcast();
        // Skip if on alpha page - MultiBroadcasterService handles it there
        if (activeBroadcast?.myRole === 'broadcaster' &&
            !window.location.pathname.startsWith('/alpha')) {
            this.activateBroadcast(activeBroadcast);
        }
        BroadcastStateService.getInstance().on('activeBroadcastChanged', (broadcast) => {
            // Skip if on alpha page - MultiBroadcasterService handles it there
            if (window.location.pathname.startsWith('/alpha')) {
                return;
            }
            if (broadcast?.myRole === 'broadcaster') {
                this.activateBroadcast(broadcast);
            }
            else {
                this.deactivateBroadcast();
            }
        });
    }
    async init() {
        // ToDo: Implement initialization
    }
    onViewerCountChanged = (count) => {
        ViewerCountService.getInstance().notifyViewerCountChangedForActiveStream(count);
    };
    onMediaStreamChanged = async () => {
        if (this.broadcastActivated && this.streamrTvBroadcaster) {
            this.streamrTvBroadcaster.changeVideoTrack(this.mediaService.getOutputMediaStream()?.getVideoTracks()[0]);
            this.streamrTvBroadcaster.changeAudioTrack(this.mediaService.getOutputMediaStream()?.getAudioTracks()[0]);
            await this.streamrTvBroadcaster.stopMediaProducer();
            const mediaStream = this.mediaService.getOutputMediaStream();
            assert(mediaStream, 'Media stream is missing');
            await this.streamrTvBroadcaster.startMediaProducer(mediaStream);
            // Resume broadcasting if it should be ongoing.
            // This is important when switching between screen share and camera.
            if (this.broadcastStarted) {
                this.streamrTvBroadcaster.startBroadcasting();
            }
        }
    };
    async createStreamrTvBroadcaster(broadcast) {
        const streamrTvBroadcaster = new StreamrTvBroadcaster({
            streamId: broadcast.streamId,
            myPrivateKey: hexToBinary(broadcast.broadcasterPrivateKey),
            aesKey: hexToBinary(broadcast.aesKey),
        });
        streamrTvBroadcaster.on('audioVolume', (volume) => {
            this.output.setVolume(volume);
        });
        streamrTvBroadcaster.on('viewerCountChanged', this.onViewerCountChanged);
        await streamrTvBroadcaster.changeVideoTrack(this.mediaService.getOutputMediaStream()?.getVideoTracks()[0]);
        await streamrTvBroadcaster.changeAudioTrack(this.mediaService.getOutputMediaStream()?.getAudioTracks()[0]);
        streamrTvBroadcaster.on('videoFrame', (videoFrame) => this.handleVideoFrame(videoFrame));
        streamrTvBroadcaster.setCanvas(this.canvas);
        streamrTvBroadcaster.on('heartbeatFromRecorder', () => {
            this.emit('heartbeatFromRecorder');
        });
        return streamrTvBroadcaster;
    }
    handleVideoFrame(videoFrame) {
        PluginApiService.getInstance().handleVideoFrame(videoFrame);
    }
    onVideoFrameProcessed = (videoFrame) => {
        this.streamrTvBroadcaster?.onVideoFrameProcessed(videoFrame);
    };
    activateBroadcast(broadcast) {
        if (this.broadcastActivated &&
            this.currentStreamId === broadcast.streamId) {
            return;
        }
        this.broadcastActivated = true;
        this.currentStreamId = broadcast.streamId;
        executeSoon(async () => {
            // Stop previous broadcaster if any
            this.streamrTvBroadcaster?.stop().catch(() => undefined);
            this.streamrTvBroadcaster =
                await this.createStreamrTvBroadcaster(broadcast);
            this.streamrTvBroadcaster
                .start()
                .then(async () => {
                this.output.setServiceRunning(true);
                // Start media producer early to get video frames for preview
                const mediaStream = this.mediaService.getOutputMediaStream();
                if (mediaStream) {
                    await this.streamrTvBroadcaster?.startMediaProducer(mediaStream);
                }
            })
                .catch((error) => {
                this.output.setServiceRunning(false);
                this.broadcastActivated = false;
                console.error('Error starting broadcast service', error);
            });
        });
        // Optional optimization for the sponsorship creation task (see the /api/create-sponsorship call in this class).
        // The stream will be created within that task if it doesn't exist yet. However, since stream creation can take some time,
        // it is better to start that process as early as possible.
        executeSoon(async () => {
            const sponsorshipBackendUrl = Env.SPONSORSHIP_BACKEND_URL;
            if (sponsorshipBackendUrl !== undefined) {
                const streamId = BroadcastStateService.getInstance().getRequiredActiveBroadcast()
                    .streamId;
                try {
                    await fetch(`${sponsorshipBackendUrl}/api/create-stream`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            streamId,
                        }),
                    });
                }
                catch (e) {
                    console.error('Error creating stream in sponsorship backend', e);
                }
            }
        });
    }
    async startBroadcast() {
        assert(this.streamrTvBroadcaster, 'StreamrTvBroadcaster not initialized');
        // Media producer should already be started, just start broadcasting
        this.streamrTvBroadcaster.startBroadcasting();
    }
    // Notifications to be called by React components
    notifyActivateBroadcast(broadcast) {
        this.activateBroadcast(broadcast);
    }
    deactivateBroadcast() {
        this.broadcastActivated = false;
        this.streamrTvBroadcaster?.stop().catch(() => undefined);
        this.streamrTvBroadcaster = undefined;
        this.canvas = undefined;
        this.output.setServiceRunning(false);
        this.broadcastStarted = false;
        this.output.setBroadcasting(false);
    }
    notifyCanvasMounted(canvas) {
        this.canvas = canvas;
        this.streamrTvBroadcaster?.setCanvas(canvas);
    }
    notifyStartBroadcast() {
        this.startBroadcast()
            .then(() => {
            this.broadcastStarted = true;
            this.output.setBroadcasting(true);
        })
            .catch((error) => {
            console.error('Error starting broadcast', error);
        });
        // Create sponsorship for the stream so that operator nodes join to this stream
        executeSoon(async () => {
            const sponsorshipBackendUrl = Env.SPONSORSHIP_BACKEND_URL;
            if (sponsorshipBackendUrl !== undefined) {
                const streamId = BroadcastStateService.getInstance().getRequiredActiveBroadcast()
                    .streamId;
                try {
                    await fetch(`${sponsorshipBackendUrl}/api/create-sponsorship`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            streamId,
                        }),
                    });
                }
                catch (e) {
                    console.error('Error creating sponsorship in sponsorship backend', e);
                }
            }
        });
    }
    notifyStopBroadcast() {
        // Just stop broadcasting, keep media producer running for preview
        this.streamrTvBroadcaster?.stopBroadcasting();
        this.broadcastStarted = false;
        this.output.setBroadcasting(false);
        this.streamrTvBroadcaster?.sendLeave();
    }
    notifyVideoQualityChanged(quality) {
        this.streamrTvBroadcaster?.setVideoEncodingQuality(quality);
        this.output.setQuality(quality);
    }
}
